import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  MessageSquare, Send, Bot, User, Loader2, Mic, MicOff,
  Volume2, VolumeX, Globe, Landmark, BookOpen, Map,
  TrendingUp, Scale, Users, Lightbulb, Search, ExternalLink,
  Telescope, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const mimeTypeCandidates = [
  'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4',
  'audio/ogg;codecs=opus', 'audio/ogg',
];
const extensionByMimeType = {
  'audio/webm;codecs=opus': 'webm', 'audio/webm': 'webm',
  'audio/mp4': 'm4a', 'audio/ogg;codecs=opus': 'ogg', 'audio/ogg': 'ogg',
};
const getRecordingMimeType = () => {
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') return '';
  return mimeTypeCandidates.find((c) => MediaRecorder.isTypeSupported(c)) || '';
};
const getFileExtension = (mt) => extensionByMimeType[mt] || 'webm';

const DEEP_DIVE_BUTTONS = [
  { id: 'political', icon: Landmark, label: 'Contexte politique', prompt: 'Donne-moi le contexte politique détaillé de ce sujet.' },
  { id: 'historical', icon: BookOpen, label: 'Contexte historique', prompt: 'Explique le contexte historique de ce sujet.' },
  { id: 'geopolitical', icon: Map, label: 'Géopolitique', prompt: 'Analyse les enjeux géopolitiques de ce sujet.' },
  { id: 'economic', icon: TrendingUp, label: 'Impact économique', prompt: 'Quels sont les impacts économiques de ce sujet ?' },
  { id: 'legal', icon: Scale, label: 'Cadre juridique', prompt: 'Quel est le cadre juridique ou réglementaire de ce sujet ?' },
  { id: 'actors', icon: Users, label: 'Acteurs clés', prompt: 'Quels sont les acteurs clés impliqués dans ce sujet ?' },
  { id: 'opinion', icon: Lightbulb, label: 'Analyse & opinions', prompt: 'Donne-moi différentes analyses et perspectives sur ce sujet.' },
  { id: 'deeper', icon: Search, label: 'Approfondir', prompt: 'Approfondi davantage ce sujet avec plus de détails et de sources.' },
];

const ChatBot = () => {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Bonjour ! Je suis l\'assistant IA d\'InfoFlow. Posez-moi vos questions sur l\'actualité, je recherche en temps réel.', showButtons: true }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isScraping, setIsScraping] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}`);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [lastTopic, setLastTopic] = useState('');
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [deepResearch, setDeepResearch] = useState(false);
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [gateEmail, setGateEmail] = useState('');
  const [gateNewsletter, setGateNewsletter] = useState(true);
  const [gateError, setGateError] = useState('');
  const [synthesis, setSynthesis] = useState(null);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingMimeTypeRef = useRef(getRecordingMimeType());

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isLoading]);

  // Fetch daily synthesis when chatbot opens
  useEffect(() => {
    if (!isOpen) return;
    fetch(`${API_URL}/api/synthesis/daily/public`)
      .then(r => r.json())
      .then(d => { if (d.available) setSynthesis(d); })
      .catch(() => {});
  }, [isOpen]);

  const handleOpen = () => {
    const saved = localStorage.getItem('chatbot_email');
    if (saved) {
      setIsOpen(true);
    } else {
      setShowEmailGate(true);
    }
  };

  const handleGateSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const value = gateEmail.trim();
    if (!emailRegex.test(value)) {
      setGateError('Adresse email invalide');
      return;
    }
    localStorage.setItem('chatbot_email', value);
    if (gateNewsletter) {
      try {
        await fetch(`${API_URL}/api/newsletter/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: value })
        });
      } catch {}
    }
    setShowEmailGate(false);
    setIsOpen(true);
  };

  const cleanupMediaStream = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const submitAudioForTranscription = useCallback(async (audioBlob, mimeType) => {
    if (!token) throw new Error('Connectez-vous pour utiliser la dictée.');
    const formData = new FormData();
    formData.append('file', new File([audioBlob], `chat-voice.${getFileExtension(mimeType)}`, { type: mimeType || 'audio/webm' }));
    formData.append('provider', 'auto'); formData.append('language', 'fr');
    formData.append('clean_text', 'true'); formData.append('target', 'chat');
    const response = await axios.post(`${API_URL}/api/ai/transcribe-audio`, formData, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
    const transcript = response.data?.text?.trim();
    if (!transcript) throw new Error('Aucune transcription.');
    setInput((prev) => `${prev}${prev ? ' ' : ''}${transcript}`.trim());
  }, [token]);

  // Speech Recognition
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const r = new SR(); r.continuous = false; r.interimResults = false; r.lang = 'fr-FR';
      r.onresult = (e) => setInput(prev => `${prev}${prev ? ' ' : ''}${e.results[0][0].transcript}`.trim());
      r.onerror = (e) => { if (e.error === 'not-allowed') toast.error('Accès micro refusé'); setIsListening(false); };
      r.onend = () => setIsListening(false);
      recognitionRef.current = r;
    }
  }, []);

  const toggleListening = () => {
    if (isVoiceProcessing) return;
    const canRecord = !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
    if (canRecord) {
      if (isListening) { mediaRecorderRef.current?.state !== 'inactive' && mediaRecorderRef.current?.stop(); return; }
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        const mt = recordingMimeTypeRef.current;
        const recorder = new MediaRecorder(stream, mt ? { mimeType: mt } : undefined);
        streamRef.current = stream; mediaRecorderRef.current = recorder; chunksRef.current = [];
        recorder.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
        recorder.onerror = () => { cleanupMediaStream(); setIsListening(false); setIsVoiceProcessing(false); };
        recorder.onstop = async () => {
          const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mt || 'audio/webm' });
          cleanupMediaStream(); setIsListening(false);
          if (!blob.size) return;
          setIsVoiceProcessing(true);
          try { await submitAudioForTranscription(blob, recorder.mimeType || mt); } catch (err) { toast.error(err.message || 'Erreur transcription'); } finally { setIsVoiceProcessing(false); }
        };
        recorder.start(); setIsListening(true);
      }).catch(() => { if (recognitionRef.current) { recognitionRef.current.start(); setIsListening(true); } });
      return;
    }
    if (!recognitionRef.current) { toast.error('Micro non supporté'); return; }
    if (isListening) recognitionRef.current.stop(); else { recognitionRef.current.start(); setIsListening(true); }
  };

  // TTS
  const speakText = useCallback((text) => {
    if (!ttsEnabled || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text.replace(/\*\*/g, '').replace(/\[.*?\]/g, ''));
    u.lang = 'fr-FR'; u.rate = 1;
    const v = window.speechSynthesis.getVoices().find(v => v.lang.startsWith('fr'));
    if (v) u.voice = v;
    u.onstart = () => setIsSpeaking(true); u.onend = () => setIsSpeaking(false); u.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(u);
  }, [ttsEnabled]);

  const stopSpeaking = () => { window.speechSynthesis.cancel(); setIsSpeaking(false); };

  const sendMessage = async (e, overrideText, isDeepDive = false) => {
    if (e) e.preventDefault();
    const text = overrideText || input.trim();
    if (!text || isLoading) return;
    if (!overrideText) setInput('');
    if (!isDeepDive) setLastTopic(text);
    const displayText = isDeepDive ? text.split(' : ').slice(1).join(' : ') || text : text;
    const newMessages = [...messages.map(m => ({ ...m, showButtons: false })), { role: 'user', content: displayText }];
    setMessages(newMessages);
    setIsLoading(true); setIsSearching(true);
    const useDeep = deepResearch || isDeepDive;
    try {
      const headers = {}; if (token) headers.Authorization = `Bearer ${token}`;
      const history = newMessages.filter(m => m.role === 'user' || m.role === 'assistant').slice(-10).map(m => ({ role: m.role, content: m.content }));
      if (useDeep) setTimeout(() => { if (isLoading) setIsScraping(true); }, 2000);
      const response = await axios.post(`${API_URL}/api/ai/chat`, { message: text, session_id: sessionId, deep_research: useDeep, history }, { headers, timeout: useDeep ? 120000 : 30000 });
      setIsSearching(false); setIsScraping(false);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response, showButtons: true, sources: response.data.sources || [], isDeepResearch: useDeep }]);
      speakText(response.data.response);
    } catch (error) {
      setIsSearching(false); setIsScraping(false);
      if (error.response?.status === 401) {
        try {
          const retry = await axios.post(`${API_URL}/api/ai/chat`, { message: text, session_id: sessionId, deep_research: false, history: [] });
          setMessages(prev => [...prev, { role: 'assistant', content: retry.data.response, showButtons: true }]);
          speakText(retry.data.response); return;
        } catch {}
      }
      setMessages(prev => [...prev, { role: 'assistant', content: 'Désolé, une erreur s\'est produite. Réessayez.', showButtons: false }]);
    } finally { setIsLoading(false); setIsSearching(false); setIsScraping(false); }
  };

  const handleDeepDive = (btn) => sendMessage(null, lastTopic ? `À propos de "${lastTopic}" : ${btn.prompt}` : btn.prompt, true);

  useEffect(() => () => { cleanupMediaStream(); recognitionRef.current?.stop(); }, [cleanupMediaStream]);

  const renderContent = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];
    let listType = null; // 'ul' or 'ol'

    const flushList = () => {
      if (listItems.length > 0) {
        const Tag = listType === 'ol' ? 'ol' : 'ul';
        const cls = listType === 'ol'
          ? 'list-decimal list-inside space-y-0.5 my-1'
          : 'list-disc list-inside space-y-0.5 my-1';
        elements.push(<Tag key={`list-${elements.length}`} className={cls}>{listItems}</Tag>);
        listItems = [];
        listType = null;
      }
    };

    const renderInline = (str) => {
      // Bold, italic, inline code, links
      const parts = str.split(/(\*\*.*?\*\*|__.*?__|_.*?_|\*[^*]+\*|`[^`]+`|\[.*?\]\(.*?\))/g);
      return parts.map((p, i) => {
        if ((p.startsWith('**') && p.endsWith('**')) || (p.startsWith('__') && p.endsWith('__'))) {
          return <strong key={i}>{p.slice(2, -2)}</strong>;
        }
        if ((p.startsWith('_') && p.endsWith('_') && p.length > 2) || (p.startsWith('*') && p.endsWith('*') && !p.startsWith('**') && p.length > 2)) {
          return <em key={i}>{p.slice(1, -1)}</em>;
        }
        if (p.startsWith('`') && p.endsWith('`')) {
          return <code key={i} className="px-1 py-0.5 bg-black/10 rounded text-[12px] font-mono">{p.slice(1, -1)}</code>;
        }
        const linkMatch = p.match(/^\[(.*?)\]\((.*?)\)$/);
        if (linkMatch) {
          return <a key={i} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" className="text-[#D62828] underline underline-offset-2">{linkMatch[1]}</a>;
        }
        return <span key={i}>{p}</span>;
      });
    };

    lines.forEach((line, i) => {
      const trimmed = line.trimStart();

      // Headers
      const headerMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
      if (headerMatch) {
        flushList();
        const level = headerMatch[1].length;
        const cls = level <= 2 ? 'font-bold text-sm mt-2 mb-0.5' : 'font-semibold text-[13px] mt-1.5 mb-0.5';
        elements.push(<div key={i} className={cls}>{renderInline(headerMatch[2])}</div>);
        return;
      }

      // Unordered list item (- or * or •)
      const ulMatch = trimmed.match(/^[-*•]\s+(.+)$/);
      if (ulMatch) {
        if (listType !== 'ul') flushList();
        listType = 'ul';
        listItems.push(<li key={i}>{renderInline(ulMatch[1])}</li>);
        return;
      }

      // Ordered list item
      const olMatch = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
      if (olMatch) {
        if (listType !== 'ol') flushList();
        listType = 'ol';
        listItems.push(<li key={i}>{renderInline(olMatch[2])}</li>);
        return;
      }

      flushList();

      // Empty line → small spacer
      if (!trimmed) {
        elements.push(<div key={i} className="h-1.5" />);
        return;
      }

      // Normal paragraph
      elements.push(<div key={i}>{renderInline(line)}</div>);
    });

    flushList();
    return elements;
  };

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <Button
          onClick={handleOpen}
          className="fixed bottom-6 right-4 sm:right-6 z-50 h-14 w-14 rounded-full shadow-lg bg-[#D62828] hover:bg-[#B91C1C]"
          size="icon"
        >
          <MessageSquare className="h-6 w-6" />
        </Button>
      )}

      {/* Email gate modal */}
      {showEmailGate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowEmailGate(false)}
        >
          <div
            className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermer — coin haut-droite, tactile friendly */}
            <button
              type="button"
              onClick={() => setShowEmailGate(false)}
              aria-label="Fermer"
              className="absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center bg-gray-100 hover:bg-red-100 dark:bg-gray-800 dark:hover:bg-red-900/40 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 pr-10">
              InfoFlow Chat
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Entrez votre email pour accéder au chat
            </p>
            <input
              type="email"
              value={gateEmail}
              onChange={(e) => { setGateEmail(e.target.value); setGateError(''); }}
              placeholder="votre@email.com"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm mb-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#D62828]"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleGateSubmit();
                if (e.key === 'Escape') setShowEmailGate(false);
              }}
              autoFocus
            />
            {gateError && (
              <p className="text-xs text-red-500 mb-2">{gateError}</p>
            )}
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={gateNewsletter}
                onChange={(e) => setGateNewsletter(e.target.checked)}
                className="accent-[#D62828]"
              />
              Recevoir la synthèse quotidienne par email
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowEmailGate(false)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleGateSubmit}
                className="flex-1 bg-[#D62828] hover:bg-[#b71c1c] text-white font-semibold py-2 rounded-lg text-sm transition-colors"
              >
                Accéder au chat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat panel — NO overlay, just a floating panel */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-50 w-full sm:w-[400px] h-[70vh] sm:h-[550px] bg-background border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-3 border-b bg-gradient-to-r from-[#D62828]/5 to-transparent flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-[#D62828] flex items-center justify-center">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold">Assistant InfoFlow</p>
                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Globe className="h-2.5 w-2.5" /> Recherche web + IA
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setDeepResearch(!deepResearch)}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-all ${
                  deepResearch ? 'bg-[#D62828] text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Telescope className="h-3 w-3" />Pro
              </button>
              <Button variant="ghost" size="icon" className="h-7 w-7"
                onClick={() => { setTtsEnabled(!ttsEnabled); if (isSpeaking) stopSpeaking(); }}>
                {ttsEnabled ? <Volume2 className="h-3.5 w-3.5 text-[#D62828]" /> : <VolumeX className="h-3.5 w-3.5 text-muted-foreground" />}
              </Button>
              <button onClick={() => setIsOpen(false)}
                className="h-8 w-8 rounded-full flex items-center justify-center bg-gray-200 hover:bg-red-100 dark:bg-gray-700 dark:hover:bg-red-900/40 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Fermer le chat">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {deepResearch && (
            <div className="px-3 py-1.5 bg-[#D62828]/5 border-b flex items-center gap-2 text-[10px] text-[#D62828] flex-shrink-0">
              <Telescope className="h-3 w-3" />
              <span className="font-medium">Recherche approfondie activée</span>
            </div>
          )}

          {/* Synthesis preview */}
          {synthesis && (
            <div className="mx-3 mt-2 mb-1 rounded-lg border border-blue-100 dark:border-blue-900 bg-blue-50/60 dark:bg-blue-950/30 px-3 py-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                  📰 Synthèse du jour
                </span>
                <a href="/synthese" target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-blue-500 hover:underline">
                  Voir tout →
                </a>
              </div>
              {synthesis.points && synthesis.points.length > 0 ? (
                <ul className="space-y-0.5">
                  {synthesis.points.slice(0, 5).map((p, i) => (
                    <li key={i} className="text-[11px] text-gray-700 dark:text-gray-300 flex gap-1.5">
                      <span className="text-blue-400 mt-0.5">•</span>
                      <span>{typeof p === 'string' ? p : p.text || p.title || ''}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11px] text-gray-600 dark:text-gray-400 line-clamp-2">
                  {synthesis.summary}
                </p>
              )}
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3" ref={scrollRef}>
            {messages.map((msg, i) => (
              <div key={i}>
                <div className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-[#D62828]/10'
                  }`}>
                    {msg.role === 'user' ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5 text-[#D62828]" />}
                  </div>
                  <div className={`rounded-lg px-3 py-2 max-w-[85%] group relative ${
                    msg.role === 'user' ? 'bg-[#D62828] text-white' : 'bg-muted'
                  }`}>
                    {msg.isDeepResearch && msg.role === 'assistant' && (
                      <div className="flex items-center gap-1 text-[9px] text-[#D62828] font-medium mb-1">
                        <Telescope className="h-2.5 w-2.5" /> Recherche approfondie
                      </div>
                    )}
                    <div className="text-[13px] leading-relaxed">{renderContent(msg.content)}</div>
                    {msg.role === 'assistant' && (
                      <button onClick={() => isSpeaking ? stopSpeaking() : speakText(msg.content)}
                        className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-background border shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Volume2 className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </div>
                </div>

                {msg.sources?.length > 0 && (
                  <div className="ml-9 mt-1.5 space-y-0.5">
                    <p className="text-[9px] text-muted-foreground font-medium flex items-center gap-1">
                      <Globe className="h-2.5 w-2.5" /> Sources ({msg.sources.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {msg.sources.slice(0, 6).map((src, si) => (
                        <a key={si} href={src.url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] rounded bg-background border hover:border-[#D62828]/30 hover:text-[#D62828] transition-colors truncate max-w-[180px]">
                          <ExternalLink className="h-2 w-2 flex-shrink-0" />
                          <span className="truncate">{src.title || (() => { try { return new URL(src.url).hostname; } catch { return src.url; } })()}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {msg.role === 'assistant' && msg.showButtons && !isLoading && (
                  <div className="ml-9 mt-2 flex flex-wrap gap-1">
                    {i === 0 ? (
                      [
                        { label: 'Actualités du jour', q: 'Quelles sont les 3 principales actualités dans le monde aujourd\'hui ?' },
                        { label: 'Derniers articles', q: 'Résume les derniers articles InfoFlow.' },
                      ].map(s => (
                        <button key={s.label} onClick={() => sendMessage(null, s.q)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded-full border border-[#D62828]/30 bg-[#D62828]/5 hover:bg-[#D62828]/15 text-[#D62828] transition-all">
                          <Search className="h-2.5 w-2.5" /> {s.label}
                        </button>
                      ))
                    ) : (
                      DEEP_DIVE_BUTTONS.slice(0, 4).map(btn => (
                        <button key={btn.id} onClick={() => handleDeepDive(btn)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded-full border border-border hover:bg-[#D62828]/10 hover:border-[#D62828]/30 hover:text-[#D62828] transition-all text-muted-foreground">
                          <btn.icon className="h-2.5 w-2.5" /> {btn.label}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-2">
                <div className="h-7 w-7 rounded-full bg-[#D62828]/10 flex items-center justify-center">
                  <Bot className="h-3.5 w-3.5 text-[#D62828]" />
                </div>
                <div className="bg-muted rounded-lg px-3 py-2">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Globe className="h-3 w-3 animate-spin text-[#D62828]" />
                      <span>{isSearching ? 'Recherche web...' : 'Rédaction...'}</span>
                    </div>
                    {isScraping && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Telescope className="h-3 w-3 animate-pulse text-[#D62828]" />
                        <span>Analyse approfondie...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-3 border-t flex gap-1.5 flex-shrink-0">
            <Button type="button" variant={isListening ? "destructive" : "ghost"} size="icon"
              className={`h-10 w-10 flex-shrink-0 ${isListening ? 'animate-pulse' : ''}`}
              onClick={toggleListening} disabled={isLoading || isVoiceProcessing}>
              {isVoiceProcessing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isListening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            </Button>
            <Input value={input} onChange={(e) => setInput(e.target.value)}
              placeholder={deepResearch ? "Recherche approfondie..." : "Posez votre question..."}
              disabled={isLoading} className="flex-1 h-10 text-sm" />
            <Button type="submit" size="icon" className="h-10 w-10 flex-shrink-0 bg-[#D62828] hover:bg-[#B91C1C]"
              disabled={isLoading || !input.trim()}>
              <Send className="h-3.5 w-3.5" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatBot;
